import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastController } from '@ionic/angular';
import { StarredSitesService } from '../starred-sites/starred-sites.service';
import { firstValueFrom } from 'rxjs';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { PortefeuilleV2ControllerApi } from '../../../api/services/portefeuille-v-2-controller-api';
import { GetPortefeuilleV2$Params } from '../../../api/fn/portefeuille-v-2-controller/get-portefeuille-v-2';
import { GetSitePortefeuilleResponseDto } from '../../../api/models/get-site-portefeuille-response-dto';
import { isTogglePortefeuillePerimetreIsoleEnabled } from '../../utils/toggle-utils';
import { GetPortefeuille$Params } from '../../../api/fn/portefeuille-v-2-controller/get-portefeuille';

export type Portefeuille = GetSitePortefeuilleResponseDto[];

@Injectable({ providedIn: 'root' })
export class PortefeuilleService {
  private readonly toastCtrl: ToastController = inject(ToastController);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly starredSitesService: StarredSitesService = inject(StarredSitesService);
  private readonly portefeuilleV2Api: PortefeuilleV2ControllerApi = inject(PortefeuilleV2ControllerApi);
  private readonly loggerFactory: LoggerFactory = inject(LoggerFactory);

  private logger: LoggerWrapper;

  constructor() {
    this.logger = this.loggerFactory.getInstance(this, false);
  }

  async getPortefeuilleOrPresentAlert(idIntervenant: string): Promise<Portefeuille> {
    try {
      if (isTogglePortefeuillePerimetreIsoleEnabled()) {
        return await this.getPortefeuillePerimetreIsole(idIntervenant);
      } else {
        return this.starredSitesService.loadStarredSites().then(async (starredSites) => {
          return await this.getPortefeuille(idIntervenant, Array.from(starredSites));
        });
      }
    } catch (err) {
      await this.presentToast(this.translate.instant('LIST_SITES.PORTEFEUILLE.ERROR'));
    }
    return Promise.resolve([]);
  }

  async getPortefeuille(idIntervenant: string, idsSite: string[] = []): Promise<GetSitePortefeuilleResponseDto[]> {
    let params: GetPortefeuilleV2$Params = { id_intervenant: idIntervenant, body: idsSite };
    try {
      return await firstValueFrom(this.portefeuilleV2Api.getPortefeuilleV2(params));
    } catch (e) {
      this.logger.error(e, "Une erreur s'est produite lors de la récupération du portefeuille");
      return Promise.reject("Une erreur s'est produite lors de la récupération du portefeuille");
    }
  }

  async getPortefeuillePerimetreIsole(idIntervenant: string): Promise<GetSitePortefeuilleResponseDto[]> {
    let params: GetPortefeuille$Params = { id_intervenant: idIntervenant };
    try {
      return await firstValueFrom(this.portefeuilleV2Api.getPortefeuille(params));
    } catch (e) {
      this.logger.error(e, "Une erreur s'est produite lors de la récupération du portefeuille périmètre isolé");
      return Promise.reject("Une erreur s'est produite lors de la récupération du portefeuille périmètre isolé");
    }
  }

  private async presentToast(message: string) {
    let toast = await this.toastCtrl.create({
      message: message,
      duration: 5000,
    });
    toast.present().then();
  }
}
